/**
 * Leaderboard API client - the game's only network calls. Talks to the small
 * Node backend in server/ (GET /api/leaderboard, POST /api/scores).
 *
 * The base URL defaults to "/api" (proxied to the local server in dev by
 * vite.config.ts, same-origin in a single-process prod deploy). Override it with
 * VITE_LEADERBOARD_API when the backend is hosted separately.
 */

const API_BASE = (import.meta.env.VITE_LEADERBOARD_API ?? "/api").replace(/\/$/, "");

/** One ranked row as returned by the server. */
export interface LeaderboardEntry {
  id: number;
  name: string;
  points: number;
  collectibles: number;
  sheepies: number;
  timeMs: number;
  rank: number;
  createdAt?: string;
}

/** The payload a finished run submits. */
export interface ScoreSubmission {
  name: string;
  points: number;
  collectibles: number;
  sheepies: number;
  timeMs: number;
}

/** Pull the ranked leaderboard. Throws on a network error or non-2xx response so
 *  callers can show a friendly error + retry. */
export async function fetchLeaderboard(limit = 100): Promise<LeaderboardEntry[]> {
  const res = await fetch(`${API_BASE}/leaderboard?limit=${limit}`, {
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Leaderboard request failed (${res.status}).`);
  const data = (await res.json()) as { leaderboard?: LeaderboardEntry[] };
  return data.leaderboard ?? [];
}

/** Submit a run. Returns the saved row's id (for highlighting) and the fresh
 *  ranked board. Throws with the server's error message on a 4xx/5xx so the
 *  submit screen can surface it and offer Retry. */
export async function submitScore(
  sub: ScoreSubmission,
): Promise<{ savedId: number; leaderboard: LeaderboardEntry[] }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/scores`, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(sub),
    });
  } catch {
    throw new Error("Could not reach the leaderboard. Check your connection and try again.");
  }
  if (!res.ok) {
    let msg = `Could not save your score (${res.status}).`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* non-JSON error body - keep the generic message */
    }
    throw new Error(msg);
  }
  const data = (await res.json()) as { savedId: number; leaderboard: LeaderboardEntry[] };
  return { savedId: data.savedId, leaderboard: data.leaderboard ?? [] };
}

/** Format a millisecond duration as m:ss for the HUD/leaderboard. */
export function formatTime(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
